import React, { useState, useEffect } from "react";
import axios from "axios";
import getAPI from "../../service/getAPI";
import { Form } from "react-bootstrap";
import { format } from "date-fns";
import OrderDetailsModal from "./OrderDetailsModal";

function OrderList() {
  const { master } = getAPI();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // State variables
  const [user, setUser] = useState(null);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [storeList, setStoreList] = useState([]);
  const [list, setList] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateFilterType, setDateFilterType] = useState("today");

  // 🟢 NEW: Credit Filters
  const [orderType, setOrderType] = useState("ALL");
  const [hasDues, setHasDues] = useState(false);

  // Debounced Search states
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(
    () => Number(localStorage.getItem("pageSize")) || 10,
  );
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const resetFilters = () => {
    setFromDate("");
    setToDate("");
    setDateFilterType("today");
    setOrderType("ALL");
    setHasDues(false);
    setSearchTerm("");
    setDebouncedSearch("");
    setPage(1);
  };

  const fetchStoreList = async (adminStoreId = null, isStore = false, storeId = null) => {
    try {
      const requestData = { pageIndex: 1, pageSize: 100 };
      const res = await axios.post(master.storelist_all, requestData);
      if (res.data.flag === 1) {
        const list = res.data.data.filter(
          (store) => store.user_type === "store" || store.user_type === "admin"
        );
        setStoreList(list);

        if (list.length > 0) {
          if (isStore && storeId) {
             setSelectedStoreId(storeId);
          } else {
             const adminStore = list.find((s) => s.store_id === adminStoreId);
             const defaultStoreId = adminStore ? adminStore.store_id : list[0].store_id;
             setSelectedStoreId(defaultStoreId);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching stores:", err);
    }
  };

  useEffect(() => {
    loadConfig();
    const storedUser = localStorage.getItem("user");
    let adminStoreId = null;
    let isStore = false;
    let storeId = null;

    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      isStore = parsed.type === "store" || parsed.user_type === "store";
      storeId = parsed.id || parsed.store_id;

      if (!isStore) {
        adminStoreId = storeId;
      }
    }
    fetchStoreList(adminStoreId, isStore, storeId);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 600);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (user && selectedStoreId && dateFilterType !== "custom") {
      loadData();
    }
    localStorage.setItem("pageSize", limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, limit, dateFilterType, debouncedSearch, orderType, hasDues, selectedStoreId]);

  const loadData = async () => {
    setLoading(true);
    let fromDateStr = "";
    let toDateStr = "";
    const today = new Date();

    if (dateFilterType === "today") {
      fromDateStr = format(today, "yyyy-MM-dd");
      toDateStr = format(today, "yyyy-MM-dd");
    } else if (dateFilterType === "thisMonth") {
      fromDateStr = format(new Date(today.getFullYear(), today.getMonth(), 1), "yyyy-MM-dd");
      toDateStr = format(today, "yyyy-MM-dd");
    } else if (dateFilterType === "custom") {
      if (fromDate) fromDateStr = format(new Date(fromDate), "yyyy-MM-dd");
      if (toDate) toDateStr = format(new Date(toDate), "yyyy-MM-dd");
    }

    try {
      const requestData = {
        page: page,
        limit: limit,
        from_date: fromDateStr,
        to_date: toDateStr,
        global_search: debouncedSearch.trim(),
        order_type: orderType,
        has_dues: hasDues,
      };

      if (selectedStoreId && selectedStoreId !== "ALL") {
        requestData.store_id = selectedStoreId;
      } else if (selectedStoreId === "ALL") {
        requestData.store_id = 0; // Prevent interceptor from injecting default
      }

      const res = await axios.post(master.paginatedOrderList, requestData);

      if (res.data.flag === 1) {
        setList(res.data.data.result);
        setTotalPages(Math.ceil(res.data.data.count / limit));
        setTotalCount(res.data.data.count);
      } else {
        if (page === 1) {
          setList([]);
          setTotalPages(0);
          setTotalCount(0);
        }
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    let fromDateStr = "";
    let toDateStr = "";
    const today = new Date();

    if (dateFilterType === "today") {
      fromDateStr = format(today, "yyyy-MM-dd");
      toDateStr = format(today, "yyyy-MM-dd");
    } else if (dateFilterType === "thisMonth") {
      fromDateStr = format(new Date(today.getFullYear(), today.getMonth(), 1), "yyyy-MM-dd");
      toDateStr = format(today, "yyyy-MM-dd");
    } else if (dateFilterType === "custom") {
      if (fromDate) fromDateStr = format(new Date(fromDate), "yyyy-MM-dd");
      if (toDate) toDateStr = format(new Date(toDate), "yyyy-MM-dd");
    }

    try {
      const requestData = {
        from_date: fromDateStr,
        to_date: toDateStr,
        global_search: debouncedSearch.trim(),
        order_type: orderType,
        has_dues: hasDues,
      };

      if (selectedStoreId && selectedStoreId !== "ALL") {
        requestData.store_id = selectedStoreId;
      } else if (selectedStoreId === "ALL") {
        requestData.store_id = 0; // Prevent interceptor from injecting default
      }

      const endpoint =
        master.exportCSV ||
        master.paginatedOrderList.replace("list", "exportcsv");

      const response = await axios.post(endpoint, requestData, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      let fileName = `Retail_Orders_${format(new Date(), "yyyy-MM-dd")}.csv`;
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetails = async (order) => {
    try {
      const response = await axios.post(master.orderListById, {
        order_id: order.order_id,
      });
      if (response.data?.flag === 1 && response.data?.data) {
        setSelectedOrder(response.data.data);
        setShowModal(true);
      } else {
        alert("Order details not found.");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      alert("Something went wrong while fetching order details.");
    }
  };

  const handleDeleteOrder = async (order) => {
    if (
      window.confirm(
        `Are you sure you want to delete Order: ${order.order_id}?`,
      )
    ) {
      try {
        const response = await axios.post(master.deleteOrder, {
          order_id: order.order_id,
        });
        if (response.data?.flag === 1) {
          alert("Order deleted successfully.");
          loadData();
        } else {
          alert("Failed to delete order. Please try again.");
        }
      } catch (error) {
        console.error("Error deleting order:", error);
        alert("Something went wrong while deleting the order.");
      }
    }
  };

  const loadConfig = async () => {
    try {
      let res = await axios.post(master.config, { configuration_id: 3 });
      if (res.data.flag === 1) setConfig(res.data.data);
    } catch (err) {
      console.error("Error fetching config data:", err);
    }
  };

  function formatDateTime(dateString) {
    const date = new Date(dateString);
    let day = String(date.getDate()).padStart(2, "0");
    let month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}/${month}/${year}, ${hours}:${minutes} ${ampm}`;
  }

  return (
    <main id="main" className="main">
      <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h1>Orders</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a>Masters</a>
              </li>
              <li className="breadcrumb-item active">Order List</li>
            </ol>
          </nav>
        </div>

        <div className="d-flex flex-wrap align-items-end mb-3 gap-3">
          {(user?.type === "admin" || user?.user_type === "admin") && (
            <div className="mb-2">
              <label
                htmlFor="employeeSelect"
                className="form-label fw-bold text-primary"
              >
                Select Store
              </label>
              <select
                id="employeeSelect"
                className="form-select shadow-sm border-primary"
                value={selectedStoreId}
                onChange={(e) => {
                  setSelectedStoreId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="ALL">All Stores</option>
                {storeList.map((store) => (
                  <option key={store.store_id} value={store.store_id}>
                    {store.store_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-2">
            <label className="form-label fw-bold text-primary">
              Global Search
            </label>
            <Form.Control
              type="text"
              placeholder="Name, mobile, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="shadow-sm border-primary"
              style={{ minWidth: "200px" }}
            />
          </div>

          <div className="mb-2">
            <label className="form-label fw-bold text-primary">
              Date Filter
            </label>
            <Form.Select
              className="shadow-sm border-primary"
              value={dateFilterType}
              onChange={(e) => {
                setDateFilterType(e.target.value);
                setPage(1);
              }}
            >
              <option value="today">Today</option>
              <option value="thisMonth">This Month</option>
              <option value="all">All Time</option>
              <option value="custom">Custom</option>
            </Form.Select>
          </div>

          {dateFilterType === "custom" && (
            <>
              <div className="mb-2">
                <label className="form-label fw-bold text-primary">
                  From Date
                </label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  max={toDate}
                  className="shadow-sm border-primary"
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-bold text-primary">
                  To Date
                </label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  min={fromDate}
                  className="shadow-sm border-primary"
                />
              </div>
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-primary fw-bold shadow-sm"
                  onClick={() => loadData()}
                >
                  Search
                </button>
              </div>
            </>
          )}

          {/* 🟢 NEW: Order Type Filter */}
          <div className="mb-2">
            <label className="form-label fw-bold text-primary">Type</label>
            <Form.Select
              className="shadow-sm border-primary"
              value={orderType}
              onChange={(e) => {
                setOrderType(e.target.value);
                setPage(1);
              }}
            >
              <option value="ALL">All</option>
              <option value="CASH">Cash</option>
              <option value="ONLINE">Online</option>
              <option value="CREDIT">Credit</option>
            </Form.Select>
          </div>

          {/* 🟢 NEW: Due Filter Switch */}
          <div
            className="mb-2 d-flex align-items-center"
            style={{ height: "38px", marginTop: "30px" }}
          >
            <Form.Check
              type="switch"
              id="has-dues-switch"
              label="Pending Dues"
              className="fw-bold text-danger ms-2"
              checked={hasDues}
              onChange={(e) => {
                setHasDues(e.target.checked);
                if (
                  e.target.checked &&
                  orderType !== "ALL" &&
                  orderType !== "CREDIT"
                ) {
                  setOrderType("CREDIT");
                }
                setPage(1);
              }}
            />
          </div>

          <div className="mb-2">
            <button
              type="button"
              className="btn btn-secondary fw-bold shadow-sm"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>

          <div className="mb-2 ms-auto">
            <button
              type="button"
              className="btn btn-success fw-bold shadow-sm"
              onClick={handleExportCSV}
              disabled={exporting || list.length === 0}
            >
              {exporting ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : (
                <i className="bi bi-filetype-csv me-2"></i>
              )}
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body mt-3">
              {loading && (
                <div className="text-center my-3">
                  <div className="spinner-grow" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
              {!loading && list.length === 0 && (
                <div className="text-center my-3">
                  <h5 className="text-muted mt-3">No data found!</h5>
                </div>
              )}

              {!loading && list.length > 0 && (
                <div className="table-responsive overflow-x-auto">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr className="table-secondary">
                        <th>No.</th>
                        <th>Name</th>
                        {selectedStoreId === "ALL" && <th>Store</th>}
                        <th>Mobile No</th>
                        <th className="text-end">Amount</th>
                        <th className="text-center">Date</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((order, index) => {
                        const totalAmount = parseFloat(order.total_amount || 0);
                        const paidAmount = parseFloat(order.paid_amount || 0);
                        const balanceDue = totalAmount - paidAmount;

                        const isCreditType =
                          order.order_type?.toLowerCase() === "credit";
                        const hasPendingDues = isCreditType && balanceDue > 0;

                        return (
                          <tr key={index}>
                            <td>{index + 1 + (page - 1) * limit}</td>
                            <td>
                              <div className="fw-bold">
                                {order.order_username || "N/A"}
                              </div>
                              <span className="badge bg-light text-dark border">
                                {order.order_type?.toUpperCase()}
                              </span>
                            </td>
                            {selectedStoreId === "ALL" && (
                              <td>
                                <span className="fw-bold text-secondary">
                                  {order.store_name || 
                                   storeList.find(s => s.store_id === order.store_id)?.store_name || 
                                   "N/A"}
                                </span>
                              </td>
                            )}
                            <td>{order.order_mobile_no || "N/A"}</td>

                            {/* 🟢 SMART AMOUNT COLUMN */}
                            <td className="text-end">
                              <div className="fw-bold fs-6">
                                ₹{totalAmount.toFixed(2)}
                              </div>
                              {hasPendingDues && (
                                <>
                                  <div
                                    className="text-success"
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Paid: ₹{paidAmount.toFixed(2)}
                                  </div>
                                  <div
                                    className="text-danger"
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Due: ₹{balanceDue.toFixed(2)}
                                  </div>
                                </>
                              )}
                            </td>

                            <td className="text-center">
                              {formatDateTime(order.created_at) || "N/A"}
                            </td>

                            {/* 🟢 SMART STATUS COLUMN */}
                            <td className="text-center">
                              {hasPendingDues ? (
                                <span
                                  className="badge"
                                  style={{ backgroundColor: "#fd7e14" }}
                                >
                                  CREDIT / DUE
                                </span>
                              ) : (
                                <span
                                  className={`badge ${
                                    order.order_status === "success"
                                      ? "bg-success"
                                      : order.order_status === "pending"
                                        ? "bg-warning text-dark"
                                        : order.order_status === "cancel"
                                          ? "bg-secondary"
                                          : "bg-danger"
                                  }`}
                                >
                                  {order.order_status
                                    ? order.order_status.toUpperCase()
                                    : "N/A"}
                                </span>
                              )}
                            </td>

                            <td className="text-center">
                              <span
                                className="fontPadding actionView cursorP me-2 text-primary"
                                onClick={() => handleViewDetails(order)}
                              >
                                <i className="bi bi-eye fs-5"></i>
                              </span>
                              <span
                                className="fontPadding actionView cursorP me-2 text-danger"
                                onClick={() => handleDeleteOrder(order)}
                              >
                                <i className="bi bi-trash fs-5"></i>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            <div className="card-footer d-flex justify-content-between align-items-center flex-wrap">
              <div className="me-auto small text-muted">
                {totalCount === 0 ? (
                  <>No orders to display</>
                ) : totalCount <= limit ? (
                  <>
                    Showing all <strong>{totalCount}</strong> orders
                  </>
                ) : (
                  <>
                    Showing <strong>{(page - 1) * limit + 1}</strong> –{" "}
                    <strong>{Math.min(page * limit, totalCount)}</strong> of{" "}
                    <strong>{totalCount}</strong> orders
                  </>
                )}
              </div>
              <div className="d-flex align-items-center me-3">
                <label htmlFor="pageSize" className="me-2 mb-0">
                  Show:
                </label>
                <select
                  id="pageSize"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="form-select d-inline-block"
                  style={{ width: "80px" }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              {totalPages > 1 && (
                <>
                  <button
                    className="btn btn-outline-primary me-2"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    &laquo; Prev
                  </button>
                  <select
                    className="form-select w-auto me-2"
                    value={page}
                    onChange={(e) => setPage(Number(e.target.value))}
                  >
                    {Array.from({ length: totalPages }, (_, i) => (
                      <option key={i} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                  >
                    Next &raquo;
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <OrderDetailsModal
        show={showModal}
        onHide={() => setShowModal(false)}
        order={selectedOrder}
      />
    </main>
  );
}

export default OrderList;
